'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import {
  Code2,
  Headphones,
  Phone,
  Megaphone,
  Globe,
  Smartphone,
  Database,
  Cloud,
  Wrench,
  Zap,
  PhoneIncoming,
  PhoneOutgoing,
  Users,
  ClipboardList,
  BarChart3,
  Mic,
  PhoneCall,
  Radio,
  Search,
  Mail,
  Target,
  TrendingUp,
  LineChart,
  Share2,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Star,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Service {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  tags?: string[];
}

interface Category {
  id: string;
  key: string;
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  headline: string;
  subtext: string;
  services: Service[];
}

const categories: Category[] = [
  {
    id: 'cat-it',
    key: 'it',
    label: 'IT & Software',
    icon: Code2,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/20',
    headline: 'End-to-end software & technology solutions',
    subtext: 'From concept to deployment — we build, integrate, and maintain the systems your business runs on.',
    services: [
      { id: 'it-1', title: 'Website Design & Development', description: 'Pixel-perfect, performant websites built for conversion and brand impact.', icon: Globe, tags: ['React', 'Next.js', 'CMS'] },
      { id: 'it-2', title: 'Web Application Development', description: 'Scalable web apps with robust backends and intuitive user interfaces.', icon: Code2, tags: ['Full-Stack', 'API', 'SaaS'] },
      { id: 'it-3', title: 'Mobile App Development', description: 'Native and cross-platform mobile apps for iOS and Android.', icon: Smartphone, tags: ['Flutter', 'React Native'] },
      { id: 'it-4', title: 'ERP / CRM Solutions', description: 'Custom enterprise resource planning and CRM systems tailored to your workflows.', icon: Database, tags: ['ERP', 'CRM', 'Automation'] },
      { id: 'it-5', title: 'API & System Integrations', description: 'Connect your tools and platforms with reliable, well-documented API integrations.', icon: Zap, tags: ['REST', 'GraphQL', 'Webhooks'] },
      { id: 'it-6', title: 'Cloud & Hosting Services', description: 'Managed cloud infrastructure, migrations, and hosting on AWS, GCP, or Azure.', icon: Cloud, tags: ['AWS', 'GCP', 'Azure'] },
      { id: 'it-7', title: 'IT Support & Maintenance', description: 'Ongoing technical support, monitoring, and proactive system maintenance.', icon: Wrench, tags: ['24/7 Support', 'SLA'] },
      { id: 'it-8', title: 'Business Automation', description: 'Automate repetitive processes to reduce costs and increase operational efficiency.', icon: Zap, tags: ['RPA', 'Workflows', 'Bots'] },
    ],
  },
  {
    id: 'cat-bpo',
    key: 'bpo',
    label: 'BPO & Call Center',
    icon: Headphones,
    color: 'text-cyan',
    bgColor: 'bg-cyan/10',
    borderColor: 'border-cyan/20',
    headline: 'Outsourced operations that scale with your business',
    subtext: 'Dedicated teams handling your customer interactions, back-office tasks, and data operations — so you can focus on growth.',
    services: [
      { id: 'bpo-1', title: 'Inbound Call Handling', description: 'Professional agents managing customer inquiries, orders, and support calls.', icon: PhoneIncoming, tags: ['24/7', 'Multi-language'] },
      { id: 'bpo-2', title: 'Outbound Campaigns', description: 'Targeted outbound calling for sales, surveys, follow-ups, and appointment setting.', icon: PhoneOutgoing, tags: ['Sales', 'Surveys'] },
      { id: 'bpo-3', title: 'Customer Support', description: 'Omnichannel customer support via phone, email, chat, and social media.', icon: Users, tags: ['Omnichannel', 'CX'] },
      { id: 'bpo-4', title: 'Technical Support', description: 'Tier-1 and Tier-2 technical helpdesk support for your products and services.', icon: Wrench, tags: ['Tier 1 & 2', 'Helpdesk'] },
      { id: 'bpo-5', title: 'Lead Generation', description: 'Qualified lead pipelines built through targeted research and outreach.', icon: Target, tags: ['B2B', 'B2C', 'Qualified'] },
      { id: 'bpo-6', title: 'Data Entry & Processing', description: 'Accurate, high-volume data entry, cleansing, and processing services.', icon: ClipboardList, tags: ['Accuracy', 'High Volume'] },
      { id: 'bpo-7', title: 'Back-Office Operations', description: 'Administrative and operational support including document processing and compliance.', icon: BarChart3, tags: ['Admin', 'Compliance'] },
      { id: 'bpo-8', title: 'Workforce Management', description: 'Agent scheduling, performance tracking, and call center workforce optimization.', icon: Users, tags: ['WFM', 'Scheduling'] },
    ],
  },
  {
    id: 'cat-voip',
    key: 'voip',
    label: 'VOIP Platform',
    icon: Phone,
    color: 'text-warning',
    bgColor: 'bg-warning/10',
    borderColor: 'border-warning/20',
    headline: 'Enterprise-grade VOIP calling infrastructure',
    subtext: 'A fully managed VOIP platform with call routing, IVR, recording, and real-time analytics — built for call centers and distributed teams.',
    services: [
      { id: 'voip-1', title: 'SIP / VOIP Calling', description: 'Crystal-clear SIP-based calling with global coverage and low latency.', icon: PhoneCall, tags: ['SIP', 'Global', 'HD Voice'] },
      { id: 'voip-2', title: 'IVR & Call Routing', description: 'Intelligent IVR menus and skill-based routing to direct callers efficiently.', icon: Radio, tags: ['IVR', 'ACD', 'Routing'] },
      { id: 'voip-3', title: 'Call Recording', description: 'Automatic call recording with secure cloud storage and compliance controls.', icon: Mic, tags: ['Compliance', 'Cloud Storage'] },
      { id: 'voip-4', title: 'Call Queues & Campaigns', description: 'Manage inbound queues and outbound dialer campaigns from one dashboard.', icon: PhoneIncoming, tags: ['Predictive Dialer', 'Queues'] },
      { id: 'voip-5', title: 'Agent Extensions', description: 'Assign dedicated extensions to agents with presence and availability controls.', icon: Users, tags: ['Extensions', 'Presence'] },
      { id: 'voip-6', title: 'Real-Time Dashboards', description: 'Live call monitoring, agent status boards, and supervisor barge-in capabilities.', icon: BarChart3, tags: ['Live Monitor', 'Supervisor'] },
      { id: 'voip-7', title: 'Reports & Analytics', description: 'Detailed call reports, agent performance metrics, and trend analytics.', icon: LineChart, tags: ['CDR', 'KPIs', 'Exports'] },
      { id: 'voip-8', title: 'Call Center Setup', description: 'End-to-end VOIP call center deployment including hardware, software, and training.', icon: Headphones, tags: ['Setup', 'Training', 'Support'] },
    ],
  },
  {
    id: 'cat-marketing',
    key: 'marketing',
    label: 'Digital Marketing',
    icon: Megaphone,
    color: 'text-success',
    bgColor: 'bg-success/10',
    borderColor: 'border-success/20',
    headline: 'Data-driven marketing that generates real results',
    subtext: 'From search rankings to paid campaigns — we build and manage marketing strategies that bring qualified traffic and measurable ROI.',
    services: [
      { id: 'mkt-1', title: 'Search Engine Optimization', description: 'Technical SEO, content strategy, and link building to rank for the terms that matter.', icon: Search, tags: ['On-Page', 'Technical', 'Link Building'] },
      { id: 'mkt-2', title: 'Google Ads Management', description: 'PPC campaign setup, optimization, and reporting to maximize your ad spend.', icon: Target, tags: ['PPC', 'Search', 'Display'] },
      { id: 'mkt-3', title: 'Social Media Marketing', description: 'Organic social strategy, content creation, and community management.', icon: Share2, tags: ['LinkedIn', 'Meta', 'X'] },
      { id: 'mkt-4', title: 'Social Media Advertising', description: 'Paid social campaigns on Meta, LinkedIn, TikTok, and more with precise targeting.', icon: Megaphone, tags: ['Meta Ads', 'LinkedIn Ads'] },
      { id: 'mkt-5', title: 'Email Marketing', description: 'Automated email sequences, newsletters, and drip campaigns that convert.', icon: Mail, tags: ['Automation', 'Drip', 'Newsletters'] },
      { id: 'mkt-6', title: 'Content Marketing', description: 'Blog posts, whitepapers, and thought leadership content that builds authority.', icon: ClipboardList, tags: ['Blog', 'Whitepapers', 'SEO Content'] },
      { id: 'mkt-7', title: 'Lead Generation Campaigns', description: 'Multi-channel lead gen funnels designed to fill your sales pipeline consistently.', icon: TrendingUp, tags: ['Funnels', 'Landing Pages'] },
      { id: 'mkt-8', title: 'Analytics & Reporting', description: 'GA4, Tag Manager, and custom dashboards to track every marketing dollar.', icon: LineChart, tags: ['GA4', 'GTM', 'Dashboards'] },
    ],
  },
];

const highlights = [
  { label: 'Services Delivered', value: '500+' },
  { label: 'Active Clients', value: '120+' },
  { label: 'Countries Served', value: '18' },
  { label: 'Team Members', value: '200+' },
];

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState<string>('it');

  const current = categories.find((c) => c.key === activeCategory) ?? categories[0];
  const CatIcon = current.icon;

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Banner */}
        <div className="relative overflow-hidden border-b border-border">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-96 h-96 blob-primary opacity-60" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 blob-accent opacity-40" />
          </div>
          <div className="relative px-6 py-12 max-w-screen-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Arrows · Service Catalog</p>
            <h1 className="text-3xl font-bold text-foreground mb-3 max-w-2xl leading-tight">
              One partner for IT, BPO, VOIP,<br />
              <span className="text-primary">and Digital Marketing</span>
            </h1>
            <p className="text-sm text-muted-foreground max-w-xl mb-8">
              Browse our full range of technology and outsourcing services. Find what fits your business, then request a quote or submit a project directly.
            </p>
            {/* Stats row */}
            <div className="flex flex-wrap gap-6">
              {highlights.map((h) => (
                <div key={h.label} className="flex flex-col">
                  <span className="text-2xl font-bold text-foreground font-tabular">{h.value}</span>
                  <span className="text-xs text-muted-foreground">{h.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-8 max-w-screen-2xl mx-auto">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all duration-150 ${
                    isActive
                      ? `${cat.bgColor} ${cat.color} ${cat.borderColor}`
                      : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-border/80'
                  }`}
                >
                  <Icon size={16} />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Category Header */}
          <div className={`card-surface p-6 mb-6 border ${current.borderColor} relative overflow-hidden`}>
            <div className="absolute top-0 right-0 w-64 h-64 opacity-5 pointer-events-none">
              <CatIcon size={256} className={current.color} />
            </div>
            <div className="relative flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3 ${current.bgColor} ${current.color}`}>
                  <CatIcon size={13} />
                  {current.label}
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">{current.headline}</h2>
                <p className="text-sm text-muted-foreground max-w-2xl">{current.subtext}</p>
              </div>
              <Link href="/client-portal/request-service">
                <button className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors whitespace-nowrap flex-shrink-0">
                  Request This Service
                  <ArrowRight size={14} />
                </button>
              </Link>
            </div>
          </div>

          {/* Services Grid — asymmetric bento-style */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {current.services.map((service, idx) => {
              const SvcIcon = service.icon;
              // Make first card span 2 cols on lg for visual hierarchy
              const isFeature = idx === 0;
              return (
                <div
                  key={service.id}
                  className={`card-surface p-5 group hover:border-primary/30 transition-all duration-200 cursor-default ${
                    isFeature ? 'lg:col-span-2' : ''
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${current.bgColor} ${current.color} group-hover:scale-105 transition-transform duration-200`}>
                    <SvcIcon size={20} />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground mb-1.5">{service.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed mb-3">{service.description}</p>
                  {service.tags && (
                    <div className="flex flex-wrap gap-1.5">
                      {service.tags.map((tag) => (
                        <span key={tag} className="text-2xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Why Arrows + CTA row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Why us — spans 2 cols */}
            <div className="lg:col-span-2 card-surface p-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Why Arrows</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { title: 'Single point of contact', desc: 'One account manager across all services — no juggling multiple vendors.' },
                  { title: 'Transparent pricing', desc: 'Detailed proposals with fixed milestones and no hidden fees.' },
                  { title: 'Proven delivery process', desc: 'Inquiry → Proposal → Approval → Build → Deploy → Maintain.' },
                  { title: 'Dedicated support', desc: 'SLA-backed support with real humans, not just ticket queues.' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <CheckCircle2 size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA card */}
            <div className="card-elevated p-6 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 blob-primary opacity-50 pointer-events-none" />
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center mb-4">
                  <Star size={20} className="text-primary" />
                </div>
                <h3 className="text-base font-bold text-foreground mb-2">Ready to get started?</h3>
                <p className="text-xs text-muted-foreground mb-6 leading-relaxed">
                  Submit your requirements and receive a detailed proposal within 24 hours.
                </p>
              </div>
              <div className="relative flex flex-col gap-2">
                <Link href="/client-portal/request-service">
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                    Request a Service
                    <ArrowRight size={14} />
                  </button>
                </Link>
                <Link href="/client-portal">
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-card border border-border text-muted-foreground rounded-lg text-sm font-medium hover:text-foreground hover:border-primary/30 transition-colors">
                    Client Portal
                    <ChevronRight size={14} />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
